import * as React from 'react';
import { ExternalLink, Link2, Plus, RefreshCw, RotateCw, Trash2 } from 'lucide-react';
import { api } from '@/lib/api-client';
import {
  Badge,
  Button,
  DropdownSelect,
  Input,
  RichTextEditor,
  SearchableSelect,
  SlideOver,
  useConfirm,
} from '@/components/ui';
import type { DropdownSelectOption, SearchableSelectOption } from '@/components/ui';
import { useI18n } from '@/lib/i18n';

type JiraIssueLink = {
  id: string;
  ticketId: string;
  jiraConfigId: string;
  issueKey: string;
  issueUrl: string;
  issueSummary: string | null;
  issueStatus: string | null;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  lastSyncError: string | null;
};

type JiraIssueType = { id: string; name: string };
type JiraProject = { id: string; key: string; name: string; issueTypes: JiraIssueType[] };
type JiraUser = { accountId: string; displayName: string; active: boolean };
type JiraOptions = {
  config: { id: string; name: string; defaultProject: string | null; defaultIssueType: string | null };
  projects: JiraProject[];
  users: JiraUser[];
  hasDevelopmentField: boolean;
};
type JiraSearchResult = {
  issueId: string;
  issueKey: string;
  issueUrl: string;
  summary: string | null;
  status: string | null;
  projectKey: string | null;
  issueType: string | null;
  priority: string | null;
};
type LinkSearchMode = 'issueKey' | 'summary';

type CreateForm = {
  projectKey: string;
  issueTypeId: string;
  issueType: string;
  development: string;
  linkedIssues: string;
  reporterAccountId: string;
  summary: string;
  description: string;
  syncEnabled: boolean;
};

function initialCreateForm(ticketSubject: string, ticketDescription: string | null): CreateForm {
  return {
    projectKey: '',
    issueTypeId: '',
    issueType: '',
    development: '',
    linkedIssues: '',
    reporterAccountId: '',
    summary: ticketSubject,
    description: ticketDescription ?? '',
    syncEnabled: true,
  };
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function mergeLink(current: JiraIssueLink[], next: JiraIssueLink): JiraIssueLink[] {
  return current.some((link) => link.id === next.id)
    ? current.map((link) => (link.id === next.id ? next : link))
    : [...current, next];
}

function splitIssueKeys(value: string): string[] {
  return [...new Set(value.split(/[\s,]+/).map((item) => item.trim().toUpperCase()).filter(Boolean))];
}

export function JiraLinksCard({
  ticketId,
  ticketSubject,
  ticketDescription,
}: {
  ticketId: string;
  ticketSubject: string;
  ticketDescription: string | null;
}) {
  const { confirm } = useConfirm();
  const { t } = useI18n();
  const requestFailed = t('tp.jira.requestFailed');
  const [links, setLinks] = React.useState<JiraIssueLink[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [panelError, setPanelError] = React.useState('');
  const [createOpen, setCreateOpen] = React.useState(false);
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [createForm, setCreateForm] = React.useState<CreateForm>(() => initialCreateForm(ticketSubject, ticketDescription));
  const [metadata, setMetadata] = React.useState<JiraOptions | null>(null);
  const [metadataLoading, setMetadataLoading] = React.useState(false);
  const [userLoading, setUserLoading] = React.useState(false);
  const [reporters, setReporters] = React.useState<JiraUser[]>([]);
  const [optionalOpen, setOptionalOpen] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [busyLinkId, setBusyLinkId] = React.useState<string | null>(null);
  const [searchMode, setSearchMode] = React.useState<LinkSearchMode>('issueKey');
  const [linkQuery, setLinkQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<JiraSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [selectedIssueKey, setSelectedIssueKey] = React.useState('');
  const metadataGeneration = React.useRef(0);
  const userGeneration = React.useRef(0);
  const searchGeneration = React.useRef(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.get<JiraIssueLink[]>(`/tickets/${ticketId}/jira-links`);
      setLinks(Array.isArray(result) ? result : []);
    } catch (loadError) {
      setError(errorMessage(loadError, requestFailed));
    } finally {
      setLoading(false);
    }
  }, [ticketId, requestFailed]);

  React.useEffect(() => { void load(); }, [load]);

  const refreshMetadata = React.useCallback(async () => {
    const generation = ++metadataGeneration.current;
    setMetadataLoading(true);
    setPanelError('');
    try {
      const result = await api.get<JiraOptions>(`/tickets/${ticketId}/jira-links/options`);
      if (generation !== metadataGeneration.current) return;
      setMetadata(result);
      setReporters(result.users ?? []);
      setCreateForm((current) => {
        const preferredProject = result.projects.find((project) => project.key === current.projectKey)
          ?? result.projects.find((project) => project.key === result.config.defaultProject)
          ?? result.projects[0];
        const preferredType = preferredProject?.issueTypes.find((type) => type.id === current.issueTypeId)
          ?? preferredProject?.issueTypes.find((type) => type.name === result.config.defaultIssueType)
          ?? preferredProject?.issueTypes[0];
        return {
          ...current,
          projectKey: preferredProject?.key ?? '',
          issueTypeId: preferredType?.id ?? '',
          issueType: preferredType?.name ?? '',
        };
      });
    } catch (loadError) {
      if (generation === metadataGeneration.current) setPanelError(errorMessage(loadError, requestFailed));
    } finally {
      if (generation === metadataGeneration.current) setMetadataLoading(false);
    }
  }, [ticketId]);

  const loadProjectContext = React.useCallback(async (projectKey: string, issueTypeId: string, userQuery = '') => {
    if (!projectKey || !issueTypeId) return;
    const generation = ++userGeneration.current;
    setUserLoading(true);
    try {
      const result = await api.get<JiraOptions>(`/tickets/${ticketId}/jira-links/options`, {
        params: { projectKey, issueTypeId, userQuery: userQuery || undefined },
      });
      if (generation !== userGeneration.current) return;
      setReporters(result.users ?? []);
      setMetadata((current) => current ? { ...current, hasDevelopmentField: result.hasDevelopmentField } : result);
    } catch (loadError) {
      if (generation === userGeneration.current) setPanelError(errorMessage(loadError, requestFailed));
    } finally {
      if (generation === userGeneration.current) setUserLoading(false);
    }
  }, [ticketId]);

  React.useEffect(() => {
    if (!createOpen) return;
    void refreshMetadata();
  }, [createOpen, refreshMetadata]);

  React.useEffect(() => {
    if (!createOpen || !createForm.projectKey || !createForm.issueTypeId) return;
    void loadProjectContext(createForm.projectKey, createForm.issueTypeId);
  }, [createOpen, createForm.projectKey, createForm.issueTypeId, loadProjectContext]);

  React.useEffect(() => {
    if (!linkOpen) return;
    const query = linkQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    let controller: AbortController | null = null;
    const timer = setTimeout(async () => {
      const generation = ++searchGeneration.current;
      controller = new AbortController();
      setSearchLoading(true);
      try {
        const result = await api.get<JiraSearchResult[]>(`/tickets/${ticketId}/jira-links/search`, {
          params: { mode: searchMode, q: query },
          signal: controller.signal,
        });
        if (generation === searchGeneration.current) setSearchResults(Array.isArray(result) ? result : []);
      } catch (searchError) {
        if (generation === searchGeneration.current && (searchError as Error).name !== 'AbortError') setPanelError(errorMessage(searchError, requestFailed));
      } finally {
        if (generation === searchGeneration.current) setSearchLoading(false);
      }
    }, 350);
    return () => {
      clearTimeout(timer);
      controller?.abort();
    };
  }, [linkOpen, linkQuery, searchMode, ticketId]);

  function openCreate() {
    setPanelError('');
    setCreateForm(initialCreateForm(ticketSubject, ticketDescription));
    setMetadata(null);
    setReporters([]);
    setOptionalOpen(true);
    setCreateOpen(true);
  }

  function closeCreate() {
    if (!saving) setCreateOpen(false);
  }

  function openLink() {
    setPanelError('');
    setSearchMode('issueKey');
    setLinkQuery('');
    setSearchResults([]);
    setSelectedIssueKey('');
    setLinkOpen(true);
  }

  function closeLink() {
    if (!saving) setLinkOpen(false);
  }

  function changeProject(projectKey: string) {
    const project = metadata?.projects.find((candidate) => candidate.key === projectKey);
    const issueType = project?.issueTypes[0];
    setCreateForm((current) => ({
      ...current,
      projectKey,
      issueTypeId: issueType?.id ?? '',
      issueType: issueType?.name ?? '',
      reporterAccountId: '',
    }));
    setMetadata((current) => current ? { ...current, hasDevelopmentField: false } : current);
    setReporters([]);
  }

  function changeIssueType(issueTypeId: string) {
    const issueType = metadata?.projects.find((project) => project.key === createForm.projectKey)?.issueTypes.find((type) => type.id === issueTypeId);
    setCreateForm((current) => ({ ...current, issueTypeId, issueType: issueType?.name ?? '', reporterAccountId: '' }));
    setMetadata((current) => current ? { ...current, hasDevelopmentField: false } : current);
    setReporters([]);
  }

  async function linkExisting(event?: React.FormEvent) {
    event?.preventDefault();
    if (!selectedIssueKey) return;
    setSaving(true);
    setPanelError('');
    try {
      const link = await api.post<JiraIssueLink>(`/tickets/${ticketId}/jira-links`, {
        issueKey: selectedIssueKey,
        syncEnabled: true,
      });
      setLinks((current) => mergeLink(current, link));
      setLinkOpen(false);
    } catch (linkError) {
      setPanelError(errorMessage(linkError, requestFailed));
    } finally {
      setSaving(false);
    }
  }

  async function createIssue(event?: React.FormEvent) {
    event?.preventDefault();
    if (!createForm.projectKey || !createForm.issueTypeId || !createForm.summary.trim()) return;
    setSaving(true);
    setPanelError('');
    try {
      const link = await api.post<JiraIssueLink>(`/tickets/${ticketId}/jira-links/create`, {
        summary: createForm.summary.trim(),
        projectKey: createForm.projectKey,
        issueType: createForm.issueType,
        issueTypeId: createForm.issueTypeId,
        development: createForm.development.trim() || undefined,
        linkedIssueKeys: splitIssueKeys(createForm.linkedIssues),
        reporterAccountId: createForm.reporterAccountId || undefined,
        description: createForm.description || undefined,
        syncEnabled: createForm.syncEnabled,
      });
      setLinks((current) => mergeLink(current, link));
      setCreateOpen(false);
    } catch (createError) {
      setPanelError(errorMessage(createError, requestFailed));
    } finally {
      setSaving(false);
    }
  }

  async function refresh(link: JiraIssueLink) {
    setBusyLinkId(link.id);
    setError('');
    try {
      const updated = await api.post<JiraIssueLink>(`/tickets/${ticketId}/jira-links/${link.id}/refresh`);
      setLinks((current) => mergeLink(current, updated));
    } catch (refreshError) {
      setError(errorMessage(refreshError, requestFailed));
    } finally {
      setBusyLinkId(null);
    }
  }

  async function unlink(link: JiraIssueLink) {
    if (!await confirm({
      title: t('tp.jira.unlinkTitle', { key: link.issueKey }),
      description: t('tp.jira.unlinkDescription'),
      confirmLabel: t('tp.jira.unlinkConfirm'),
    })) return;
    setBusyLinkId(link.id);
    setError('');
    try {
      await api.delete(`/tickets/${ticketId}/jira-links/${link.id}`);
      setLinks((current) => current.filter((item) => item.id !== link.id));
    } catch (unlinkError) {
      setError(errorMessage(unlinkError, requestFailed));
    } finally {
      setBusyLinkId(null);
    }
  }

  const selectedProject = metadata?.projects.find((project) => project.key === createForm.projectKey);
  const projectOptions: DropdownSelectOption[] = (metadata?.projects ?? []).map((project) => ({
    value: project.key,
    label: project.name,
    description: project.key,
  }));
  const issueTypeOptions: DropdownSelectOption[] = (selectedProject?.issueTypes ?? []).map((issueType) => ({
    value: issueType.id,
    label: issueType.name,
  }));
  const reporterOptions: SearchableSelectOption[] = [
    { value: '', label: t('tp.jira.unassigned') },
    ...reporters.filter((user) => user.active).map((user) => ({ value: user.accountId, label: user.displayName })),
  ];

  return (
    <div className="space-y-3">
      {error && <p className="rounded-md border border-danger-200 bg-danger-50 px-2.5 py-2 text-xs text-danger-700" role="alert">{error}</p>}
      {loading ? (
        <div className="space-y-2" aria-live="polite">
          <div className="h-4 animate-pulse rounded bg-line" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-line" />
        </div>
      ) : (
        <>
          {links.length > 0 && (
            <div className="space-y-2">
              {links.map((link) => (
                <div key={link.id} className="rounded-lg border border-line bg-white p-3 shadow-card">
                  <div className="flex items-start gap-2">
                    <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <a href={link.issueUrl} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-1 font-semibold text-brand-700 hover:underline">
                        <span className="truncate">{link.issueKey}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                      </a>
                      {link.issueSummary && <p className="mt-1 line-clamp-2 text-xs text-ink-muted">{link.issueSummary}</p>}
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {link.issueStatus && <Badge variant="neutral">{link.issueStatus}</Badge>}
                        <Badge variant={link.syncEnabled ? 'success' : 'outline'}>{link.syncEnabled ? t('tp.jira.syncOn') : t('tp.jira.syncOff')}</Badge>
                      </div>
                      {link.lastSyncError && <p className="mt-2 break-words text-xs text-warning-700">{t('tp.jira.lastSync', { error: link.lastSyncError })}</p>}
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end gap-1.5">
                    <Button type="button" size="xs" variant="outline" loading={busyLinkId === link.id} onClick={() => void refresh(link)}>
                      <RefreshCw aria-hidden="true" /> {t('tp.jira.refresh')}
                    </Button>
                    <Button type="button" size="xs" variant="ghost" disabled={busyLinkId !== null} onClick={() => void unlink(link)}>
                      <Trash2 aria-hidden="true" /> {t('tp.jira.unlink')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {links.length === 0 && <p className="rounded-md border border-dashed border-line-strong px-3 py-3 text-xs text-ink-subtle">{t('tp.jira.noLinks')}</p>}
          <div className="grid gap-2 border-t border-line pt-3">
            <Button type="button" variant="outline" className="justify-start bg-surface-subtle text-left" onClick={openCreate}>
              <Plus aria-hidden="true" /> {t('tp.jira.createIssue')}
            </Button>
            <Button type="button" variant="outline" className="justify-start bg-surface-subtle text-left" onClick={openLink}>
              <Link2 aria-hidden="true" /> {t('tp.jira.linkIssue')}
            </Button>
          </div>
        </>
      )}

      <SlideOver
        open={createOpen}
        onClose={closeCreate}
        title={t('tp.jira.createTitle')}
        widthClassName="max-w-lg"
        footer={(
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" disabled={saving} onClick={closeCreate}>{t('tp.common.cancel')}</Button>
            <Button type="button" loading={saving} disabled={!createForm.projectKey || !createForm.issueTypeId || !createForm.summary.trim()} onClick={() => void createIssue()}>
              {t('tp.jira.createAndLink')}
            </Button>
          </div>
        )}
      >
        <form className="space-y-5 p-5" onSubmit={(event) => void createIssue(event)}>
          {panelError && <p className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700" role="alert">{panelError}</p>}
          <div className="flex items-center justify-between border-b border-line pb-4">
            <button type="button" onClick={() => setOptionalOpen((open) => !open)} className="inline-flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800">
              <Plus className="h-4 w-4" aria-hidden="true" /> {optionalOpen ? t('tp.jira.hideOptional') : t('tp.jira.addOptional')}
            </button>
            <Button type="button" size="icon" variant="outline" loading={metadataLoading} onClick={() => void refreshMetadata()} aria-label={t('tp.jira.refreshFields')} title={t('tp.jira.refreshFields')}>
              <RotateCw aria-hidden="true" />
            </Button>
          </div>

          <DropdownSelect label={t('tp.jira.projects')} options={projectOptions} value={createForm.projectKey} onChange={changeProject} placeholder={metadataLoading ? t('tp.jira.loadingProjects') : t('tp.jira.selectProject')} loading={metadataLoading} searchable />
          <DropdownSelect label={t('tp.jira.issueType')} options={issueTypeOptions} value={createForm.issueTypeId} onChange={changeIssueType} placeholder={metadataLoading ? t('tp.jira.loadingIssueTypes') : t('tp.jira.selectIssueType')} disabled={!createForm.projectKey} searchable />

          {optionalOpen && (
            <>
              <Input label={t('tp.jira.development')} value={createForm.development} onChange={(event) => setCreateForm((current) => ({ ...current, development: event.target.value }))} placeholder={metadataLoading ? t('tp.jira.loadingFields') : metadata?.hasDevelopmentField ? t('tp.jira.enterDevelopment') : t('tp.jira.notAvailableProject')} disabled={metadataLoading || !metadata?.hasDevelopmentField} />
              <Input label={t('tp.jira.linkedIssues')} value={createForm.linkedIssues} onChange={(event) => setCreateForm((current) => ({ ...current, linkedIssues: event.target.value }))} placeholder={t('tp.jira.linkedIssuesPlaceholder')} hint={t('tp.jira.linkedIssuesHint')} />
            </>
          )}

          <SearchableSelect label={t('tp.jira.reporter')} options={reporterOptions} value={createForm.reporterAccountId} onChange={(value) => setCreateForm((current) => ({ ...current, reporterAccountId: value }))} onSearch={(query) => void loadProjectContext(createForm.projectKey, createForm.issueTypeId, query)} placeholder={userLoading ? t('tp.jira.loadingUsers') : t('tp.jira.searchUsers')} loading={userLoading} />
          <Input label={t('tp.jira.summary')} value={createForm.summary} onChange={(event) => setCreateForm((current) => ({ ...current, summary: event.target.value }))} required />
          <RichTextEditor label={t('tp.jira.description')} value={createForm.description} onChange={(value) => setCreateForm((current) => ({ ...current, description: value }))} minHeight="190px" placeholder={t('tp.jira.describePlaceholder')} />
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            <input type="checkbox" checked={createForm.syncEnabled} onChange={(event) => setCreateForm((current) => ({ ...current, syncEnabled: event.target.checked }))} className="h-4 w-4 rounded border-line-strong" />
            {t('tp.jira.syncStatus')}
          </label>
        </form>
      </SlideOver>

      <SlideOver
        open={linkOpen}
        onClose={closeLink}
        title={t('tp.jira.linkTitle')}
        widthClassName="max-w-lg"
        footer={(
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" disabled={saving} onClick={closeLink}>{t('tp.common.cancel')}</Button>
            <Button type="button" loading={saving} disabled={!selectedIssueKey} onClick={() => void linkExisting()}>{t('tp.jira.linkIssue')}</Button>
          </div>
        )}
      >
        <form className="space-y-5 p-5" onSubmit={(event) => void linkExisting(event)}>
          {panelError && <p className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700" role="alert">{panelError}</p>}
          <fieldset>
            <legend className="mb-3 text-sm font-semibold text-ink">{t('tp.jira.searchBy')}</legend>
            <div className="flex flex-wrap gap-4">
              {(['issueKey', 'summary'] as const).map((mode) => (
                <label key={mode} className="flex items-center gap-2 text-sm text-ink-muted">
                  <input type="radio" name="jira-link-search-mode" checked={searchMode === mode} onChange={() => { setSearchMode(mode); setLinkQuery(''); setSearchResults([]); setSelectedIssueKey(''); }} />
                  {mode === 'issueKey' ? t('tp.jira.issueId') : t('tp.jira.issueSummary')}
                </label>
              ))}
            </div>
          </fieldset>
          <Input label={t('tp.jira.searchIssue')} value={linkQuery} onChange={(event) => { setLinkQuery(event.target.value); setSelectedIssueKey(''); }} placeholder={t('tp.jira.searchIssuePlaceholder')} />
          <div className="min-h-40 border-t border-line pt-4">
            {searchLoading && <p className="text-sm text-ink-subtle">{t('tp.jira.searching')}</p>}
            {!searchLoading && linkQuery.trim().length >= 2 && searchResults.length === 0 && <p className="text-sm text-ink-subtle">{t('tp.jira.noneFound')}</p>}
            <div className="space-y-2">
              {searchResults.map((result) => (
                <button key={result.issueKey} type="button" onClick={() => setSelectedIssueKey(result.issueKey)} className={`w-full rounded-md border px-3 py-3 text-left transition-colors ${selectedIssueKey === result.issueKey ? 'border-brand-500 bg-brand-50' : 'border-line hover:bg-surface-hover'}`}>
                  <span className="block font-semibold text-brand-700">{result.issueKey}</span>
                  <span className="mt-1 block text-sm text-ink">{result.summary || t('tp.jira.untitled')}</span>
                  <span className="mt-1 block text-xs text-ink-subtle">{[result.projectKey, result.issueType, result.status].filter(Boolean).join(' · ')}</span>
                </button>
              ))}
            </div>
          </div>
        </form>
      </SlideOver>
    </div>
  );
}
